import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  commonData,
  DntLeaderDetail,
  DntProjectResponse,
  EmployeeData,
  EmployeeResponse,
  hiringRequestDDValues,
  HiringRequestResponseData,
  LobResponse,
  ProjectData,
  ProjectDetail,
  ProjectResponse,
  SkillResponse,
} from '@interfaces/hiring-manager.model';
import { VendorDetails } from '@interfaces/vendor.model';
import { HiringManagerLevels } from './hiring-request.config';
import { sortDropdownValues } from '@utils/validators';


export const getProjectNameOptions = (projects: DntLeaderDetail) => {
  return sortDropdownValues(
    Object.entries(projects.projectDetails).map(([key, value]) => ({
      label: value.toString(),
      value: key,
    })),
  );
};

export const getProjectDropdownOptions = (projects: ProjectResponse) => {
  return sortDropdownValues(
    projects.data.project.map((project: ProjectData) => {
      return { label: project?.name ?? '', value: project?.id ?? '' };
    }),
  );
};
export const getPojectIdOptions = (projects: ProjectData[]) => {
  return projects
    ?.filter((item: ProjectData) => item.id !== null)
    ?.map((item: ProjectData) => ({
      label: item.name,
      value: item.id,
    }));
};

export const getLobOptions = (lobRes: LobResponse) => {
  return sortDropdownValues(
    lobRes.data.lob?.map((item: commonData) => ({
      label: item.name,
      value: item.id,
    })),
  );
};

export const getDntOptions = (dntRes: DntProjectResponse) => {
  return sortDropdownValues(
    dntRes?.data?.dntLeaderDetails?.map((item: DntLeaderDetail) => ({
      label: item.dntLeaderName,
      value: item.dntLeaderEmpId,
    })),
  );
};

export const getDntLeaderOptions = (
  hiringReq: HiringRequestResponseData,
  employees: EmployeeResponse,
) => {
  const dntLeader = employees.data.find(
    (item: EmployeeData) => item.employeeId === hiringReq.leaderEmployeeId,
  );
  return [
    {
      label: dntLeader?.employeeName ?? '',
      value: dntLeader?.employeeId ?? '',
    },
  ];
};

export const getHiringManagerOptions = (empRes: EmployeeResponse) => {
  return sortDropdownValues(
    empRes.data
      ?.filter(
        (item: EmployeeData) =>
          HiringManagerLevels.includes(item.level) && item.poiType === 'EMP',
      )
      ?.map((item: EmployeeData) => ({
        label: item.employeeName,
        value: item.employeeId,
      })),
  );
};

export const getpanelMemberOptions = (empRes: EmployeeResponse) => {
  return sortDropdownValues(
    empRes.data?.map((item: EmployeeData) => ({
      label: item.employeeName,
      value: item.employeeId,
    })),
  );
};

export const getSkillOptions = (skillRes: SkillResponse) => {
  return sortDropdownValues(
    skillRes.data.skill?.map((item: commonData) => ({
      label: item.name,
      value: item.id,
    })),
  );
};

export const getVendorOptions = (vendorRes: VendorDetails[]) => {
  return sortDropdownValues(
    vendorRes
      ?.filter((item: commonData) => item.name !== 'null')
      ?.map((item: VendorDetails) => ({
        label: item.name,
        value: item.id,
        isGlobalVendor: item.isGlobalVendor === true,
      })),
  );
};

export function patchHiringRequestFormValues(
  hiringRequest: HiringRequestResponseData,
  hiringRequestForm: FormGroup,
  lobOptions: hiringRequestDDValues[] = [],
  DntOptions: hiringRequestDDValues[] = [],
  projectNameOptions: hiringRequestDDValues[] = [],
  projectIdOptions: hiringRequestDDValues[] = [],
  jobLocation: hiringRequestDDValues[] = [],
  priorityOptions: hiringRequestDDValues[] = [],
  mandatorySkillOptions: hiringRequestDDValues[] = [],
  gthSkillOptions: hiringRequestDDValues[] = [],
  hiringManagerOptions: hiringRequestDDValues[] = [],
  panelMemberOneOptions: hiringRequestDDValues[] = [],
  panelMemberTwoOptions: hiringRequestDDValues[] = [],
  panelMemberThreeOptions: hiringRequestDDValues[] = [],
  timeZoneOneOptions: hiringRequestDDValues[] = [],
  timeZoneTwoOptions: hiringRequestDDValues[] = [],
  timeZoneThreeOptions: hiringRequestDDValues[] = [],
  vendorOptions: hiringRequestDDValues[] = [],
) {
  const selectedLob = lobOptions?.find(
    (option) => option.value == hiringRequest.lobId,
  );
  const selectedDnt = DntOptions?.find(
    (option) => option.value == hiringRequest.leaderEmployeeId,
  );
  const selectedProjName = projectNameOptions?.find(
    (option) => option.label == hiringRequest.project?.projectName,
  );
  const selectedJobLocation = jobLocation?.filter((loc) =>
    hiringRequest.location?.includes(loc.label),
  );
  const selectedpriority = priorityOptions?.filter((priority) =>
    hiringRequest.isPriority?.toString()?.includes(priority?.value?.toString()),
  );
  const selectedStartDate = hiringRequest.desiredStartDate
    ? new Date(hiringRequest.desiredStartDate).toISOString().split('T')[0]
    : '';
  const selectedMandSkill =
    mandatorySkillOptions?.filter((option) =>
      hiringRequest.hiringSkills?.some(
        (skill) =>
          skill?.mandatorySkills &&
          skill?.categoryId?.toString() === option.value.toString(),
      ),
    ) || [];
  const selectedGthSkill =
    gthSkillOptions?.filter((option) =>
      hiringRequest.hiringSkills?.some(
        (skill) =>
          skill?.goodToHaveSkills &&
          skill?.categoryId?.toString() === option.value.toString(),
      ),
    ) || [];
  const selectedHiringManager = hiringManagerOptions?.find(
    (option) =>
      option.value == hiringRequest?.hiringManagerRequest?.hiringManagerEmpId,
  );
  const selectedHiringManagerEmail =
    hiringRequest.hiringManagerRequest?.hiringManagerEmail;
  const selectedPanelMemberOne = panelMemberOneOptions?.find(
    (option) => option.value == hiringRequest.hiringPanel[0]?.employeeId,
  );
  const selectedPanelMemberTwo = panelMemberTwoOptions?.find(
    (option) => option.value == hiringRequest.hiringPanel[1]?.employeeId,
  );
  const selectedPanelMemberThree = panelMemberThreeOptions?.find(
    (option) => option.value == hiringRequest.hiringPanel[2]?.employeeId,
  );
  const selectedTimezoneOne = timeZoneOneOptions?.find(
    (option) =>
      option.value == hiringRequest.hiringPanel[0]?.panelAvailabilityTimezone,
  );
  const selectedTimezoneTwo = timeZoneTwoOptions?.find(
    (option) =>
      option.value == hiringRequest.hiringPanel[1]?.panelAvailabilityTimezone,
  );
  const selectedTimezoneThree = timeZoneThreeOptions?.find(
    (option) =>
      option.value == hiringRequest.hiringPanel[2]?.panelAvailabilityTimezone,
  );
  const selectedVendor =
    vendorOptions?.filter((option) =>
      hiringRequest.hiringVendors?.some(
        (vendor) => vendor.vendorId.toString() === option.value.toString(),
      ),
    ) || [];

  hiringRequestForm.patchValue({
    lineOfBusiness: selectedLob ? selectedLob : '',
    dntLeader: selectedDnt ? selectedDnt : '',
    jobTitle: hiringRequest.jobTitle|| '',
    projectName: selectedProjName ? selectedProjName : '',
    numberOfPositions: hiringRequest.numberOfPositions || '',
    jobLocation: selectedJobLocation ? selectedJobLocation : '',
    priorityOptions: selectedpriority ? selectedpriority : '',
    overAllExp: hiringRequest.yearsOfOverallExperience || '',
    relExp: hiringRequest.yearsOfRelevantExperience || '',
    startDate: selectedStartDate,
    mandatorySkills: selectedMandSkill ? selectedMandSkill : '',
    goodToHaveSkills: selectedGthSkill ? selectedGthSkill : '',
    comment: hiringRequest.additionalComments || '',
    hiringManager: selectedHiringManager ? selectedHiringManager : '',
    hirManEmail: selectedHiringManagerEmail ? selectedHiringManagerEmail : '',
    panelMemberOne: selectedPanelMemberOne ? selectedPanelMemberOne : '',
    panelMemberTwo: selectedPanelMemberTwo ? selectedPanelMemberTwo : '',
    panelMemberThree: selectedPanelMemberThree ? selectedPanelMemberThree : '',
    panelMemberOneTimezone: selectedTimezoneOne ? selectedTimezoneOne : '',
    panelMemberTwoTimezone: selectedTimezoneTwo ? selectedTimezoneTwo : '',
    panelMemberThreeTimezone: selectedTimezoneThree
      ? selectedTimezoneThree
      : '',
    vendors: selectedVendor ? selectedVendor : '',
  });
}

// Custom validator to check if a file is selected or already uploaded
export function jobDescriptionValidator(
  selectedFile: string | null,
): ValidatorFn {
  return (_control: AbstractControl): { [key: string]: any } | null => {
    return selectedFile ? null : { required: true };
  };
}

export function getFieldError(control: AbstractControl | null): string {
  if (control?.errors) {
    if (control.errors['required']) {
      return 'This field cannot be blank';
    }
    if (control.errors['invalidDateFormat']) {
      return 'Invalid date format';
    }
    if (control.errors['invalidDate']) {
      return 'Start date should be at least 3 days from today';
    }
    if (control.errors['minlength']) {
      const minLength = control.errors['minlength'].requiredLength;
      return `This field must be at least ${minLength} characters long.`;
    }
    if (control.errors['min']) {
      const min = control.errors['min'].min;
      return `Minimum value should be ${min}.`;
    }
    if (control.errors['email']) {
      return `Invalid email format.`;
    }
    if (control.errors['pattern']) {
      return `Numbers only`;
    }
  }
  return '';
}

export function setupPanelMemberValidators(hiringRequestForm: FormGroup) {
  addPanelMemberValidator(
    hiringRequestForm,
    'panelMemberTwo',
    'panelMemberTwoTimezone',
  );
  addPanelMemberValidator(
    hiringRequestForm,
    'panelMemberThree',
    'panelMemberThreeTimezone',
  );
}
function addPanelMemberValidator(
  hiringRequestForm: FormGroup,
  panelMemberControlName: string,
  panelMemberTimezoneControlName: string,
) {
  const panelMemberControl = hiringRequestForm.get(panelMemberControlName);
  const panelMemberTimezoneControl = hiringRequestForm.get(
    panelMemberTimezoneControlName,
  );

  const updateValidators = () => {
    const panelMemberValue = panelMemberControl?.value;
    const panelMemberTimezoneValue = panelMemberTimezoneControl?.value;

    if (panelMemberValue || panelMemberTimezoneValue) {
      panelMemberControl?.setValidators([Validators.required]);
      panelMemberTimezoneControl?.setValidators([Validators.required]);
    } else {
      panelMemberControl?.removeValidators([Validators.required]);
      panelMemberTimezoneControl?.removeValidators([Validators.required]);
    }
    panelMemberControl?.updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
    panelMemberTimezoneControl?.updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
  };

  panelMemberControl?.valueChanges.subscribe(() => {
    updateValidators();
  });

  panelMemberTimezoneControl?.valueChanges.subscribe(() => {
    updateValidators();
  });

  updateValidators();
}

  export function disableFields(form: FormGroup, fields: string[]): void {
    fields.forEach(field => {
      const control = form.get(field);
      if (control) {
        control.disable();
      }
    });
  }
 
  export function getFormValuesWithDisabledControls(form: FormGroup, fields: string[]): any {
    fields.forEach(field => {
      const control = form.get(field);
      if (control) {
        control.enable({ emitEvent: false });
      }
    });
 
    const formValues = form.getRawValue();
 
    fields.forEach(field => {
      const control = form.get(field);
      if (control) {
        control.disable({ emitEvent: false });
      }
    });
    return formValues;
  }
  
export const getDynamicPanelMemberOptions = (
  empRes: EmployeeResponse,
  selectedPanelMembers: (string | number)[] = []
): hiringRequestDDValues[] => {
  return sortDropdownValues(
    empRes.data
      ?.filter((item: EmployeeData) => !selectedPanelMembers.includes(item.employeeId))
      .map((item: EmployeeData) => ({
        label: item.employeeName,
        value: item.employeeId,
      }))
  );
};

  export function panelMemberPairValidator(): ValidatorFn {
  return (formArray: AbstractControl) => {
    if (!(formArray instanceof FormArray)) return null;
    let hasError = false;
    formArray.controls.forEach((group: AbstractControl, idx: number) => {
      const member = group.get('panelMember')?.value;
      const timezone = group.get('panelMemberTimezone')?.value;
      if ((!!member && !timezone) || (!member && !!timezone)) {
        if (!member) group.get('panelMember')?.setErrors({ requiredPair: true });
        if (!timezone) group.get('panelMemberTimezone')?.setErrors({ requiredPair: true });
        hasError = true;
      } else {
        group.get('panelMember')?.setErrors(null);
        group.get('panelMemberTimezone')?.setErrors(null);
      }
    });
    return hasError ? { panelMemberPair: true } : null;
  };
}